import React from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '../../../../components/base/PageLayout';
const TableManners: React.FC = () => {
  React.useEffect(() => {
    document.title = 'テーブルマナーの美学 - 執事が教えるフルコース・和食・ワインの作法｜執事ベル';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', '執事ベルが伝授する本格的なテーブルマナー。フルコースディナーでのカトラリーの使い方、ナプキンの扱い、ワイングラスの持ち方から、和食の箸使いと器の扱いまで、西洋料理・日本料理の正統派マナーを徹底解説。結婚式、接待、フォーマルディナーなど、大切な食事の場で自信を持って振る舞える知識と、マナーの本質である「美しさ」の追求をお伝えします。');
    }
  }, []);


  return (
    <PageLayout hero={<>
<div className="relative h-96 bg-gradient-to-r from-gray-900 to-gray-700 flex items-center justify-center">
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-30"
            style={{
              backgroundImage: `url('/images/gakken/gk-18.jpg')`
            }}
          />
          <div className="relative z-10 text-center text-white max-w-6xl mx-auto px-6">
            <h1 className="text-5xl md:text-6xl font-serif mb-6 tracking-wide">
              テーブルマナーの美学
            </h1>
            <p className="text-xl md:text-2xl font-light italic opacity-90">
              The Art of Dining Etiquette
            </p>
          </div>
        </div>
</>}>
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        

        {/* Article Content */}
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="prose prose-lg max-w-none">
            {/* Introduction */}
            <div className="mb-12 p-8 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg border-l-4 border-amber-600">
              <p className="text-lg leading-relaxed text-gray-800 mb-0">
                テーブルマナーは単なる食事の作法ではありません。それは相手への敬意を表し、共に過ごす時間を美しく演出する芸術なのです。正しいマナーを身につけることで、どのような場面でも自信を持って振る舞うことができるようになります。
              </p>
            </div>

            {/* Main Content */}
            <div className="space-y-12">
              <section>
                <h2 className="text-3xl font-serif text-gray-900 mb-6 border-b-2 border-amber-600 pb-3">
                  基本的な席次とエスコート
                </h2>
                <div className="grid md:grid-cols-2 gap-8 items-center mb-8">
                  <div>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      フォーマルなディナーでは、席次が重要な意味を持ちます。主賓は主催者の右側に座り、次に重要なゲストは左側に配置されます。
                    </p>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      女性をエスコートする際は、レストランでは男性が先に入り、席まで案内します。椅子を引いて差し上げるのも大切なマナーの一つです。
                    </p>
                  </div>
                  <div className="relative">
                    <img 
                      src="/images/generated/seq-table-escort-da7dfd.jpg"
                      alt="エスコートマナー"
                      className="w-full h-80 object-cover rounded-lg shadow-lg"
                    />
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-3xl font-serif text-gray-900 mb-6 border-b-2 border-amber-600 pb-3">
                  カトラリーの正しい使い方
                </h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  カトラリーの使い方は、その人の品格を表す重要な要素です。基本的なルールを理解し、美しい所作を心がけましょう。
                </p>
                
                <div className="bg-white border border-gray-200 rounded-lg p-6 mb-8">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">カトラリーの基本ルール</h3>
                  <div className="space-y-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-8 h-8 bg-amber-700 text-white rounded-full flex items-center justify-center font-semibold text-sm">1</div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">外側から使用</h4>
                        <p className="text-gray-700">コースの順番に従って、外側のカトラリーから使用していきます</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-4">
                      <div className="w-8 h-8 bg-amber-700 text-white rounded-full flex items-center justify-center font-semibold text-sm">2</div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">持ち方の基本</h4>
                        <p className="text-gray-700">フォークは左手、ナイフは右手。人差し指で軽く押さえるように持ちます</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-4">
                      <div className="w-8 h-8 bg-amber-700 text-white rounded-full flex items-center justify-center font-semibold text-sm">3</div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">休憩時の置き方</h4>
                        <p className="text-gray-700">食事中の休憩時は、ナイフとフォークを「ハの字」に置きます</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-4">
                      <div className="w-8 h-8 bg-amber-700 text-white rounded-full flex items-center justify-center font-semibold text-sm">4</div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">食事終了時</h4>
                        <p className="text-gray-700">食事が終わったら、ナイフとフォークを揃えて4時の位置に置きます</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-8 items-center">
                  <div className="relative">
                    <img 
                      src="/images/generated/seq-table-cutlery-262db3.jpg"
                      alt="カトラリーの正しい置き方"
                      className="w-full h-80 object-cover rounded-lg shadow-lg"
                    />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">美しい食べ方のコツ</h3>
                    <ul className="space-y-3 text-gray-700">
                      <li className="flex items-start space-x-3">
                        <i className="ri-check-line text-amber-600 mt-1"></i>
                        <span>一口サイズに切って食べる</span>
                      </li>
                      <li className="flex items-start space-x-3">
                        <i className="ri-check-line text-amber-600 mt-1"></i>
                        <span>音を立てずに静かに食べる</span>
                      </li>
                      <li className="flex items-start space-x-3">
                        <i className="ri-check-line text-amber-600 mt-1"></i>
                        <span>背筋を伸ばして美しい姿勢を保つ</span>
                      </li>
                      <li className="flex items-start space-x-3">
                        <i className="ri-check-line text-amber-600 mt-1"></i>
                        <span>口に食べ物がある時は話さない</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-3xl font-serif text-gray-900 mb-6 border-b-2 border-amber-600 pb-3">
                  ワインとグラスのマナー
                </h2>
                <div className="grid md:grid-cols-2 gap-8 items-center mb-8">
                  <div>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      ワインのマナーは、フォーマルなディナーにおいて特に重要です。グラスの持ち方から乾杯の作法まで、細やかな配慮が求められます。
                    </p>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      ワイングラスは必ずステム（脚）の部分を持ち、ボウル部分には触れないようにします。これはワインの温度を保つためと、グラスに指紋をつけないためです。
                    </p>
                  </div>
                  <div className="relative">
                    <img 
                      src="/images/generated/seq-table-wine-8cc638.jpg"
                      alt="ワイングラスの持ち方"
                      className="w-full h-80 object-cover rounded-lg shadow-lg"
                    />
                  </div>
                </div>

                <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-8 rounded-lg mb-8">
                  <h3 className="text-xl font-semibold text-gray-900 mb-6">ワインマナーのポイント</h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="flex items-center space-x-3">
                        <i className="ri-wine-glass-line text-amber-600 text-xl"></i>
                        <span className="text-gray-800">グラスはステムを持つ</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <i className="ri-hand-heart-line text-amber-600 text-xl"></i>
                        <span className="text-gray-800">乾杯時は目を見て行う</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <i className="ri-drop-line text-amber-600 text-xl"></i>
                        <span className="text-gray-800">適量を心がける</span>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div className="flex items-center space-x-3">
                        <i className="ri-time-line text-amber-600 text-xl"></i>
                        <span className="text-gray-800">ゆっくりと味わう</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <i className="ri-chat-3-line text-amber-600 text-xl"></i>
                        <span className="text-gray-800">会話を楽しみながら</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <i className="ri-shield-check-line text-amber-600 text-xl"></i>
                        <span className="text-gray-800">節度を保つ</span>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-3xl font-serif text-gray-900 mb-6 border-b-2 border-amber-600 pb-3">
                  会話とタイミング
                </h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  食事中の会話は、その場の雰囲気を左右する重要な要素です。適切な話題選びと、食事のペースに合わせた会話のタイミングが大切です。
                </p>
                
                <div className="grid md:grid-cols-2 gap-8">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-green-800 mb-4">
                      <i className="ri-thumb-up-line mr-2"></i>
                      適切な話題
                    </h3>
                    <ul className="space-y-2 text-green-700">
                      <li>• 文化や芸術について</li>
                      <li>• 旅行の体験談</li>
                      <li>• 季節の話題</li>
                      <li>• 料理やワインについて</li>
                      <li>• 共通の趣味や関心事</li>
                    </ul>
                  </div>
                  
                  <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-red-800 mb-4">
                      <i className="ri-thumb-down-line mr-2"></i>
                      避けるべき話題
                    </h3>
                    <ul className="space-y-2 text-red-700">
                      <li>• 政治的な議論</li>
                      <li>• 宗教的な話題</li>
                      <li>• 個人的な金銭問題</li>
                      <li>• 病気や不幸な出来事</li>
                      <li>• 他人の悪口や批判</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-3xl font-serif text-gray-900 mb-6 border-b-2 border-amber-600 pb-3">
                  国際的なマナーの違い
                </h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  グローバル化が進む現代では、様々な文化的背景を持つ方々と食事を共にする機会が増えています。基本的なマナーを理解しつつ、相手の文化への配慮も大切です。
                </p>
                
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-amber-800 mb-3">
                    <i className="ri-lightbulb-line mr-2"></i>
                    執事ベルからのアドバイス
                  </h3>
                  <p className="text-amber-700 leading-relaxed">
                    「テーブルマナーの本質は、相手への思いやりです。完璧な作法を覚えることも大切ですが、最も重要なのは、共に食事をする方々が心地よく過ごせるよう配慮することです。自然体でありながら、品格を保つ。それが真のテーブルマナーなのです。」
                  </p>
                </div>
              </section>
            </div>

            {/* Related Articles */}
            <div className="mt-16 pt-12 border-t border-gray-200">
              <h3 className="text-2xl font-serif text-gray-900 mb-8">関連記事</h3>
              <div className="grid md:grid-cols-3 gap-6">
                <a href="/wisdom/articles/dress-code" className="group block bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-300">
                  <img 
                    src="/images/generated/seq-related-dress-a0745e.jpg"
                    alt="ドレスコードの基本"
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-4">
                    <h4 className="font-semibold text-gray-900 group-hover:text-amber-600 transition-colors">ドレスコードの基本</h4>
                    <p className="text-sm text-gray-600 mt-2">場面に応じた適切な装いの選び方</p>
                  </div>
                </a>
                
                <a href="/wisdom/articles/social-etiquette" className="group block bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-300">
                  <img 
                    src="/images/generated/seq-related-social-5bb9e7.jpg"
                    alt="社交のエチケット"
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-4">
                    <h4 className="font-semibold text-gray-900 group-hover:text-amber-600 transition-colors">社交のエチケット</h4>
                    <p className="text-sm text-gray-600 mt-2">パーティーや社交場での振る舞い方</p>
                  </div>
                </a>
                
                <a href="/wisdom/articles/communication" className="group block bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-300">
                  <img 
                    src="/images/generated/seq-related-comm2-46ced1.jpg"
                    alt="コミュニケーション術"
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-4">
                    <h4 className="font-semibold text-gray-900 group-hover:text-amber-600 transition-colors">コミュニケーション術</h4>
                    <p className="text-sm text-gray-600 mt-2">心に響く会話と信頼関係の築き方</p>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default TableManners;
